import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import type { SiteHealthNoScoreReason } from '@/lib/siteHealth.types'
import { isPresent } from '@/lib/utils'
import { CARD_CLASS, EYEBROW_CLASS, scoreTone } from './SiteHealthStyles'
import type { DiscoveryScoreCardProps } from './siteHealthComponents.types'

const NO_SCORE_EXPLANATIONS: Record<SiteHealthNoScoreReason, string> = {
  insufficient_evidence:
    'There was not enough eligible evidence to produce a reliable score.',
  no_scored_evaluations: 'No scored checks were evaluated in this audit.',
}

function noScoreExplanation(
  reason: SiteHealthNoScoreReason | null | undefined
) {
  return (
    (reason && NO_SCORE_EXPLANATIONS[reason]) ??
    'A score is not available for this audit yet.'
  )
}

function canShowScoreDelta(
  scoringVersion: string,
  comparisonScoringVersion: string | null | undefined
) {
  if (!comparisonScoringVersion || !scoringVersion) return true

  return scoringVersion === comparisonScoringVersion
}

function scoreDeltaMetadata(delta: number | null | undefined) {
  if (!isPresent(delta)) {
    return { icon: Minus, tone: 'text-muted-foreground' }
  }

  if (delta > 0) return { icon: ArrowUp, tone: 'text-emerald-600' }

  if (delta < 0) return { icon: ArrowDown, tone: 'text-red-600' }

  return { icon: Minus, tone: 'text-muted-foreground' }
}

export default function DiscoveryScoreCard({
  overview,
}: DiscoveryScoreCardProps) {
  const score = overview.score
  const delta = overview.scoreDelta
  const deltaMetadata = scoreDeltaMetadata(delta)
  const DeltaIcon = deltaMetadata.icon
  const showDelta =
    isPresent(score) &&
    isPresent(delta) &&
    canShowScoreDelta(
      overview.scoringVersion,
      overview.comparisonScoringVersion
    )

  return (
    <section className={`${CARD_CLASS} p-6`}>
      <div className="flex items-start justify-between gap-4">
        <p className={EYEBROW_CLASS}>Discovery score</p>
        {showDelta && (
          <span
            className={`inline-flex items-center gap-1 text-sm font-medium ${deltaMetadata.tone}`}
          >
            <DeltaIcon className="size-4" aria-hidden="true" />
            {delta > 0 ? '+' : ''}
            {delta} since comparison
          </span>
        )}
      </div>
      {isPresent(score) ? (
        <>
          <div className="mt-4 flex items-baseline gap-3">
            <span
              className={`text-6xl font-semibold tabular-nums tracking-tight ${scoreTone(score.score)}`}
            >
              {score.score}
            </span>
            <span className="text-2xl text-muted-foreground">
              / {score.max}
            </span>
          </div>
          <Progress
            aria-label="Discovery score"
            className="mt-5"
            value={score.percent}
          />
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Your Discovery score shows how easily AI systems can access,
            understand, and use content from your site.
          </p>
        </>
      ) : (
        <div className="mt-4 space-y-1 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Score not available</p>
          <p>{noScoreExplanation(overview.noScoreReason)}</p>
        </div>
      )}
    </section>
  )
}
